import { Box, Button, Card, Icon, Stack, Typography } from '@mui/material';
import { Facility, SchoolYear } from '@schooly/api';
import { ArrowRightIcon, CheckIcon, PlusIcon } from '@schooly/style';
import { formatList } from '@schooly/utils/common';
import { TimetableStructureSnapshot } from 'libs/api/src/lib/generated/schooly-api-v2';
import { FC, Fragment, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

interface TimetableSetupStepsProps {
  schoolYears: SchoolYear[];
  structures: TimetableStructureSnapshot[];
  rooms: Facility[];
  isAdmin: boolean;
}

export const TimetableSetupSteps: FC<TimetableSetupStepsProps> = ({
  schoolYears,
  structures,
  rooms,
  isAdmin,
}) => {
  const { formatMessage } = useIntl();

  const steps = useTimetableSetupSteps({
    schoolYearsNames: formatList(schoolYears.map((year) => year.name)),
    structuresNames: formatList(structures.map((structure) => structure.name)),
    roomsCount: rooms.length,
    isAdmin,
  });

  return (
    <Stack alignItems="center" justifyContent="center" height="100%" gap={3}>
      <Typography variant="h1" textAlign="center">
        <FormattedMessage id="school-timetables-Setup-Title" />
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={1} width="100%">
        {steps.map((step, index) => (
          <Fragment key={step.linkTo}>
            {index > 0 && <TimetableSetupStepArrow />}
            <TimetableSetupStepCard
              label={step.label}
              buttonText={step.buttonText}
              linkTo={step.linkTo}
              disabled={step.disabled}
              isComplete={step.isComplete}
              stepLabel={`${formatMessage({ id: 'school-timetables-Setup-Step' })} ${index + 1}`}
            />
          </Fragment>
        ))}
      </Stack>
    </Stack>
  );
};

interface TimetableSetupStepCardProps {
  label: string;
  buttonText: string;
  linkTo: string;
  disabled?: boolean;
  isComplete?: boolean;
  stepLabel: string;
}

const TimetableSetupStepCard: FC<TimetableSetupStepCardProps> = ({
  label,
  buttonText,
  linkTo,
  disabled = false,
  isComplete = false,
  stepLabel,
}) => {
  const getCardBackgroundColor = useCallback(() => {
    if (isComplete) return 'success.superLight';
    if (!disabled) return 'background.default';
  }, [isComplete, disabled]);

  return (
    <Card
      sx={(theme) => ({
        p: 1.5,
        backgroundColor: getCardBackgroundColor(),
        border: disabled && !isComplete ? theme.mixins.borderValue() : 'none',
        flex: 1,
        minWidth: 210,
        maxWidth: 'calc((100% - 85px) / 4)',
      })}
    >
      <Stack justifyContent="space-between" height={150}>
        <Stack gap={0.5}>
          <Typography variant="h4" color="common.grey3">
            {stepLabel}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              whiteSpace: 'pre-line',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
        </Stack>

        {isComplete ? (
          <DoneIconCircle />
        ) : (
          !disabled && (
            <Link to={linkTo}>
              <Button startIcon={<PlusIcon />}>{buttonText}</Button>
            </Link>
          )
        )}
      </Stack>
    </Card>
  );
};

const DoneIconCircle: FC = () => (
  <Box
    sx={(theme) => ({
      height: 44,
      width: 44,
      borderRadius: '50%',
      border: theme.mixins.borderValue(),
      borderColor: 'success.main',
      backgroundColor: 'background.paper',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'success.main',
    })}
  >
    <Icon>
      <CheckIcon />
    </Icon>
  </Box>
);

const TimetableSetupStepArrow: FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
    <ArrowRightIcon />
  </Box>
);

interface GetTimetableSetupStepLabelKeyParams {
  prefix: string;
  isComplete: boolean;
  hasAccess?: boolean;
}

const getTimetableSetupStepLabelKey = ({
  prefix,
  isComplete,
  hasAccess,
}: GetTimetableSetupStepLabelKeyParams): string => {
  if (isComplete) return `${prefix}-CompleteTitle`;
  if (hasAccess === false) return `${prefix}-NoAccessTitle`;
  return `${prefix}-Title`;
};

interface UseTimetableSetupStepsParams {
  isAdmin: boolean;
  schoolYearsNames: string;
  structuresNames: string;
  roomsCount: number;
}

interface TimetableSetupStep {
  label: string;
  buttonText: string;
  linkTo: string;
  disabled?: boolean;
  isComplete?: boolean;
}

const useTimetableSetupSteps = ({
  isAdmin,
  schoolYearsNames,
  structuresNames,
  roomsCount,
}: UseTimetableSetupStepsParams): TimetableSetupStep[] => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    const hasSchoolYear = !!schoolYearsNames;
    const hasStructure = !!structuresNames;
    const hasRooms = !!roomsCount;

    return [
      {
        label: formatMessage(
          {
            id: getTimetableSetupStepLabelKey({
              prefix: 'school-timetables-Setup-SchoolYears',
              isComplete: hasSchoolYear,
              hasAccess: isAdmin,
            }),
          },
          { schoolYears: schoolYearsNames },
        ),
        buttonText: formatMessage({ id: 'school-timetables-Setup-SchoolYears-Create' }),
        linkTo: '/settings/school_years',
        disabled: !isAdmin,
        isComplete: hasSchoolYear,
      },
      {
        label: formatMessage(
          {
            id: getTimetableSetupStepLabelKey({
              prefix: 'school-timetables-Setup-Structures',
              isComplete: hasStructure,
              hasAccess: true,
            }),
          },
          { structures: structuresNames },
        ),
        buttonText: formatMessage({ id: 'school-timetables-Setup-Structures-Create' }),
        linkTo: '/settings/timetable_structures/new',
        isComplete: hasStructure,
      },
      {
        label: formatMessage(
          {
            id: getTimetableSetupStepLabelKey({
              prefix: 'school-timetables-Setup-Facilities',
              isComplete: hasRooms,
              hasAccess: isAdmin,
            }),
          },
          { count: roomsCount },
        ),
        buttonText: formatMessage({ id: 'school-timetables-Setup-Facilities-Create' }),
        linkTo: '/settings/facilities',
        disabled: !isAdmin,
        isComplete: hasRooms,
      },
      {
        label: formatMessage({
          id:
            hasSchoolYear && hasStructure
              ? 'school-timetables-Setup-Timetables-ReadyTitle'
              : 'school-timetables-Setup-Timetables-Title',
        }),
        buttonText: formatMessage({ id: 'school-timetables-Setup-Timetables-Create' }),
        linkTo: '/settings/timetables/new',
        disabled: !hasSchoolYear || !hasStructure,
      },
    ];
  }, [isAdmin, schoolYearsNames, structuresNames, roomsCount, formatMessage]);
};
